import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ShieldCheck, Lock, Fingerprint, ArrowRight, Copy, Check } from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  
  const { login, user, twoFactorPending, verify2FA, cancel2FA } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    navigate("/");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.requires_2fa) {
        toast.info(result.setup_required 
          ? "Please set up two-factor authentication" 
          : "Enter your authentication code"
        );
      } else {
        toast.success(`Welcome back, ${result.name}!`);
        if (result.must_reset_password) {
          navigate("/reset-password");
        } else {
          navigate("/");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      const userData = await verify2FA(totpCode);
      toast.success(`Welcome back, ${userData.name}!`);
      
      if (userData.must_reset_password) {
        navigate("/reset-password");
      } else {
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
      setTotpCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSetup = () => {
    cancel2FA();
    setTotpCode("");
    setPassword("");
  };

  const copySecret = () => {
    if (twoFactorPending?.secret) {
      navigator.clipboard.writeText(twoFactorPending.secret);
      setCopiedSecret(true);
      toast.success("Secret copied to clipboard");
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  // Color Palette
  const colors = {
    // Primary
    primaryBlue: '#0082B0',
    primaryLight: '#0EA5E9',
    primaryDark: '#0284C7',
    // Dark Theme
    background: '#0A1520',
    cardBg: '#1E293B',
    inputBg: '#0F172A',
    border: '#334155',
    // Text
    lightText: '#F1F5F9',
    mutedText: '#94A3B8',
    subtleText: '#64748B',
    // Accent
    infoBlue: '#38BDF8',
  };

  // Security badges component
  const SecurityBadges = () => (
    <div className="flex items-center justify-center gap-6 mt-8 text-xs" style={{ color: colors.mutedText }}>
      <div className="flex items-center gap-1.5">
        <Lock className="h-3.5 w-3.5" />
        <span style={{ fontFamily: "'DM Sans', sans-serif" }}>256-bit SSL</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Fingerprint className="h-3.5 w-3.5" />
        <span style={{ fontFamily: "'DM Sans', sans-serif" }}>MFA Protected</span>
      </div>
      <div className="flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5" />
        <span style={{ fontFamily: "'DM Sans', sans-serif" }}>Bank-level Security</span>
      </div>
    </div>
  );

  // Show 2FA verification/setup screen
  if (twoFactorPending) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono&display=swap');
        `}</style>
        <div 
          className="min-h-screen flex items-center justify-center p-6"
          style={{ backgroundColor: colors.background }}
          data-testid="2fa-page"
        >
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <img
                src="/smj-logo-transparent.png"
                alt="Steele Martin Jones CPAs"
                className="h-20 object-contain mb-4"
              />
              <div 
                className="text-sm tracking-[0.3em] font-medium"
                style={{ color: colors.infoBlue, fontFamily: "'DM Sans', sans-serif" }}
              >
                TWO-FACTOR AUTHENTICATION
              </div>
            </div>

            {/* 2FA Card */}
            <div 
              className="rounded-2xl p-8"
              style={{ backgroundColor: colors.cardBg }}
            >
              <h2 
                className="text-2xl font-semibold mb-2"
                style={{ color: colors.lightText, fontFamily: "'Fraunces', serif" }}
              >
                {twoFactorPending.totp_setup_required ? "Set up authenticator" : "Enter verification code"}
              </h2>
              <p 
                className="text-sm mb-6"
                style={{ color: colors.mutedText, fontFamily: "'DM Sans', sans-serif" }}
              >
                {twoFactorPending.totp_setup_required 
                  ? "Scan the QR code with your authenticator app"
                  : "Enter the 6-digit code from your authenticator app"
                }
              </p>

              {/* QR Code for setup */}
              {twoFactorPending.totp_setup_required && twoFactorPending.qr_code && (
                <div className="mb-6">
                  <div className="bg-white p-4 rounded-lg w-fit mx-auto mb-4">
                    <img
                      src={twoFactorPending.qr_code}
                      alt="2FA QR Code"
                      className="w-48 h-48"
                    />
                  </div>
                  
                  <div className="text-center">
                    <p className="text-xs mb-2" style={{ color: colors.mutedText, fontFamily: "'DM Sans', sans-serif" }}>
                      Or enter this code manually:
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <code 
                        className="px-3 py-1.5 rounded text-sm"
                        style={{ 
                          backgroundColor: colors.inputBg, 
                          color: colors.infoBlue,
                          fontFamily: "'JetBrains Mono', monospace"
                        }}
                      >
                        {twoFactorPending.secret}
                      </code>
                      <button
                        onClick={copySecret}
                        className="p-1.5 rounded transition-colors hover:opacity-80"
                        style={{ color: colors.mutedText }}
                      >
                        {copiedSecret ? (
                          <Check className="h-4 w-4 text-green-400" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleVerify2FA} className="space-y-5">
                <div>
                  <label 
                    className="block text-xs uppercase tracking-wider mb-2"
                    style={{ color: colors.mutedText, fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Verification Code
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    className="h-14 text-center text-2xl tracking-[0.5em] text-white placeholder:text-slate-600"
                    style={{ 
                      backgroundColor: colors.inputBg,
                      borderColor: colors.border,
                      fontFamily: "'JetBrains Mono', monospace"
                    }}
                    data-testid="totp-input"
                    autoComplete="one-time-code"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 text-base font-medium transition-all duration-200 text-white"
                  style={{ 
                    background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`,
                    fontFamily: "'DM Sans', sans-serif"
                  }}
                  disabled={loading || totpCode.length !== 6}
                  data-testid="verify-2fa-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      {twoFactorPending.totp_setup_required ? "Complete Setup" : "Verify & Continue"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleCancelSetup}
                  className="w-full text-sm transition-colors py-2 hover:opacity-80"
                  style={{ color: colors.mutedText, fontFamily: "'DM Sans', sans-serif" }}
                >
                  Back to Login
                </button>
              </form>
            </div>

            <SecurityBadges />
          </div>
        </div>
      </>
    );
  }

  // Regular login form
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono&display=swap');
      `}</style>
      <div 
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: colors.background }}
        data-testid="login-page"
      >
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/smj-logo-transparent.png"
              alt="Steele Martin Jones CPAs"
              className="h-20 object-contain mb-4"
            />
            <div 
              className="text-sm tracking-[0.3em] font-medium"
              style={{ color: colors.infoBlue, fontFamily: "'DM Sans', sans-serif" }}
            >
              EMPLOYEE PORTAL
            </div>
          </div>

          {/* Login Card */}
          <div 
            className="rounded-2xl p-8"
            style={{ backgroundColor: colors.cardBg }}
          >
            <h2 
              className="text-2xl font-semibold mb-2"
              style={{ color: colors.lightText, fontFamily: "'Fraunces', serif" }}
            >
              Sign in to your portal
            </h2>
            <p 
              className="text-sm mb-6"
              style={{ color: colors.mutedText, fontFamily: "'DM Sans', sans-serif" }}
            >
              Your financial data is encrypted and protected.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label 
                  className="block text-xs uppercase tracking-wider mb-2"
                  style={{ color: colors.mutedText, fontFamily: "'DM Sans', sans-serif" }}
                >
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-14 text-white placeholder:text-slate-500"
                  style={{ 
                    backgroundColor: colors.inputBg,
                    borderColor: colors.border,
                    fontFamily: "'DM Sans', sans-serif"
                  }}
                  data-testid="email-input"
                />
              </div>

              <div>
                <label 
                  className="block text-xs uppercase tracking-wider mb-2"
                  style={{ color: colors.mutedText, fontFamily: "'DM Sans', sans-serif" }}
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-14 text-white placeholder:text-slate-500 pr-12"
                    style={{ 
                      backgroundColor: colors.inputBg,
                      borderColor: colors.border,
                      fontFamily: "'DM Sans', sans-serif"
                    }}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors hover:opacity-80"
                    style={{ color: colors.mutedText }}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link 
                  to="/forgot-password"
                  className="text-sm transition-colors hover:opacity-80"
                  style={{ color: colors.primaryLight }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-14 text-base font-medium transition-all duration-200 text-white hover:opacity-90"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`,
                  fontFamily: "'DM Sans', sans-serif"
                }}
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </div>

          <SecurityBadges />
        </div>
      </div>
    </>
  );
};

export default Login;
